import { useMemo } from 'react'

import { EShowItemCategory } from '@pkg/lib-data'

import { ShowsCard } from '../../components/product-card/shows-card'
import { emptyStateButtonClicked, filterClicked, seeAllButtonClicked } from '../../lib/analytics'
import type { PageContext } from '../../lib/context/base'
import { filterShowsDetailsByContext } from '../../lib/context/shows-context'
import { getShowsCategoryUrl, limitShowsCards } from '../../lib/limit/shows-limit'
import {
  SHOW_TYPE_SLUG_MAP,
  buildCategoryUrl,
  getCategoryFromPathOrContext,
  getSlugFromCategory,
} from '../../lib/show-types'
import { SHOWS_CATEGORY_ID } from '../../lib/shows-config'
import { sortShowsCards } from '../../lib/sorting/shows-sorting'
import type { ProductCardDataShows } from '../../lib/transforms/shows'
import type { ProductDetailsDataShows } from '../../lib/transforms/shows/details'

interface ShowsControllerProps {
  context: PageContext
  regularList: ProductCardDataShows[]
  productsMapFiltered: Record<string, ProductDetailsDataShows>
  viewState: 'loading' | 'loaded' | 'noData' | 'hasError'
}

export const ShowsController = (props: ShowsControllerProps) => {
  const { context, regularList, productsMapFiltered, viewState } = props

  // Get current category from URL (for highlighting selected filter)
  const selectedCategory = useMemo(() => {
    return getCategoryFromUrl(context.type)
  }, [context.type])

  // Filter ProductDetailsDataShows by context first (using predicates like client)
  const filteredDetailsResult = useMemo(() => {
    if (viewState !== 'loaded' || !productsMapFiltered || Object.keys(productsMapFiltered).length === 0) {
      return { filtered: [], isSharonFallback: false }
    }

    // Convert map to array for filtering
    const detailsArray = Object.values(productsMapFiltered) as ProductDetailsDataShows[]

    // Filter using predicate-based filtering (same as client)
    return filterShowsDetailsByContext(detailsArray, context)
  }, [viewState, productsMapFiltered, context])

  const filteredDetails = filteredDetailsResult.filtered
  const isSharonFallback = filteredDetailsResult.isSharonFallback

  // Create cards from filtered details (like client does)
  const filteredCards = useMemo(() => {
    if (viewState !== 'loaded' || filteredDetails.length === 0) {
      return []
    }

    // We need catalogsContentData to create cards
    // Since we don't have direct access, we'll filter the existing regularList
    // by matching IDs from filteredDetails
    const filteredIds = new Set(
      filteredDetails.map((d: ProductDetailsDataShows) => {
        return d.computed.id
      }),
    )

    return regularList.filter((card) => {
      return filteredIds.has(card.computed.id)
    })
  }, [viewState, filteredDetails, regularList])

  // Extract available categories from the filtered shows (after context filtering)
  const availableCategories = useMemo(() => {
    if (viewState !== 'loaded' || filteredCards.length === 0) {
      return []
    }

    return extractAvailableCategories(filteredCards)
  }, [viewState, filteredCards])

  const cards = useMemo(() => {
    if (viewState !== 'loaded' || filteredCards.length === 0) {
      return { cards: [], hasMore: false, seeAllUrl: getShowsCategoryUrl() }
    }

    // Filter by selected category
    const categoryFiltered = filterByCategory(filteredCards, selectedCategory)

    // Sort shows
    const sorted = sortShowsCards(categoryFiltered)

    // Limit to 12 items
    const limited = limitShowsCards(sorted)

    return limited
  }, [viewState, filteredCards, selectedCategory])

  // Check if we're showing Sharon fallback results
  const isShowingFallbackResults = useMemo(() => {
    if (viewState !== 'loaded') {
      return false
    }

    // Only check for central region
    if (context.region !== 'central') {
      return false
    }

    return isSharonFallback
  }, [viewState, context.region, isSharonFallback])

  // Use title from context if provided, otherwise generate region-specific title
  const getTitle = () => {
    // If no cards, show special title
    if (cards.cards.length === 0) {
      return 'להופעות במחירים מטורפיםםם:'
    }

    if (context.title) {
      return context.title
    }
    if (context.region === 'central') {
      return 'הופעות זמינות כעת במרכז'
    }

    return 'מופעים קרובים'
  }

  const handleEmptyStateClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (typeof window !== 'undefined') {
      const screenName = window.location.pathname + window.location.search

      emptyStateButtonClicked({ screenName })
    }
  }

  const handleSeeAllClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (typeof window !== 'undefined') {
      const screenName = window.location.pathname + window.location.search

      seeAllButtonClicked({ screenName })
    }
  }

  const handleFilterClick = (filterName: string) => {
    if (typeof window !== 'undefined') {
      const screenName = window.location.pathname + window.location.search

      filterClicked({
        filterName,
        screenName,
      })
    }
  }

  const getAllFilterUrl = () => {
    if (typeof window === 'undefined') return '#'
    const currentPath = window.location.pathname

    // Get all valid category slugs
    const validSlugs = Object.values(SHOW_TYPE_SLUG_MAP)

    // Extract the last segment of the path
    const lastSegment = currentPath.split('/').filter(Boolean).pop() || ''

    // Check if the last segment is a category slug
    const isCategorySlug = validSlugs.includes(lastSegment)

    // If it's a category slug, remove it to get base path
    // Otherwise, keep the current path as base (e.g., /contents/shows/central)
    const basePath = isCategorySlug ? currentPath.replace(/\/[^/]+$/, '') || currentPath : currentPath

    return buildCategoryUrl(basePath, null)
  }

  const getCategoryUrl = (category: { id: string; label: string }) => {
    if (typeof window === 'undefined') return '#'
    const currentPath = window.location.pathname
    const isSelected = selectedCategory === category.id

    // Get all valid category slugs
    const validSlugs = Object.values(SHOW_TYPE_SLUG_MAP)

    // Extract the last segment of the path
    const lastSegment = currentPath.split('/').filter(Boolean).pop() || ''

    // Check if the last segment is a category slug
    const isCategorySlug = validSlugs.includes(lastSegment)

    // If it's a category slug, remove it to get base path
    // Otherwise, keep the current path as base (e.g., /contents/shows/central)
    const basePath = isCategorySlug ? currentPath.replace(/\/[^/]+$/, '') || currentPath : currentPath

    if (isSelected) {
      // Remove category from path (go to base)
      return buildCategoryUrl(basePath, null)
    } else {
      // Add category slug to path
      // category.id is Hebrew name, need to convert to slug
      const categoryEnum = Object.values(EShowItemCategory).find((cat) => {
        return cat === category.id
      })

      if (categoryEnum) {
        const categorySlug = getSlugFromCategory(categoryEnum)

        return buildCategoryUrl(basePath, categorySlug)
      }

      return basePath
    }
  }

  // Show empty state when: no data from API OR data loaded but filtered to zero cards
  const shouldShowEmptyState =
    viewState === 'noData' || (viewState === 'loaded' && (filteredCards.length === 0 || cards.cards.length === 0))

  if (viewState === 'loading') {
    return (
      <section className="bg-muted/30 px-4 py-16">
        <div className="container mx-auto max-w-[var(--container-desktop-width,930px)]">
          <div className="py-12 text-center">
            <div className="text-muted-foreground text-lg [direction:rtl]">טוען...</div>
          </div>
        </div>
      </section>
    )
  }

  if (shouldShowEmptyState) {
    return (
      <section className="bg-muted/30 px-4 py-8 [direction:rtl]">
        <div className="container mx-auto max-w-[1064px]">
          <div className="text-center">
            <div className="mb-6 text-[30px] font-bold text-black [direction:rtl]">להופעות במחירים מטורפיםםם:</div>
            <a
              href={cards.seeAllUrl}
              onClick={handleEmptyStateClick}
              className="ring-offset-background focus-visible:ring-ring bg-background inline-flex h-10 items-center justify-center gap-2 rounded-full border border-[#e2e8f0] !bg-[rgb(242,100,110)] px-6 py-3 text-[18px] font-bold whitespace-nowrap text-white no-underline transition-colors [direction:rtl] hover:border-black hover:!bg-[rgb(242,100,110)] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
            >
              לחצו כאן
            </a>
          </div>
        </div>
      </section>
    )
  }

  if (viewState === 'hasError') {
    return (
      <section className="bg-muted/30 px-4 py-16">
        <div className="container mx-auto max-w-[var(--container-desktop-width,930px)]">
          <div className="py-12 text-center">
            <div className="text-muted-foreground text-lg [direction:rtl]">שגיאה בטעינת הנתונים</div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="bg-muted/30 px-4 py-16 [direction:rtl]">
      <div className="container mx-auto max-w-[1064px]">
        <div className="mb-8 text-center">
          <h2 className="text-foreground mb-4 text-3xl font-bold [direction:rtl] md:text-4xl">{getTitle()}</h2>
        </div>

        {/* Category Filters - Dynamically generated from available shows */}
        {availableCategories.length > 0 && (
          <div className="mb-12 flex flex-wrap justify-center gap-3 [direction:rtl]">
            {/* "All" filter button - positioned at the rightmost position (first in RTL) */}
            <a
              href={getAllFilterUrl()}
              onClick={() => {
                return handleFilterClick('הכל')
              }}
              className={`ring-offset-background focus-visible:ring-ring bg-background inline-flex h-10 items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-medium whitespace-nowrap no-underline transition-colors [direction:rtl] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 ${
                selectedCategory === null
                  ? 'border-[#e2e8f0] !bg-[rgb(242,100,110)] text-white hover:!bg-[rgb(242,100,110)]'
                  : 'border border-[#e2e8f0] hover:bg-[#f1f5f9]'
              }`}
              aria-pressed={selectedCategory === null}
            >
              הכל
            </a>
            {availableCategories.map((category) => {
              const isSelected = selectedCategory === category.id
              const href = getCategoryUrl(category)

              return (
                <a
                  key={category.id}
                  href={href}
                  onClick={() => {
                    return handleFilterClick(category.label)
                  }}
                  className={`ring-offset-background focus-visible:ring-ring bg-background inline-flex h-10 items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-medium whitespace-nowrap no-underline transition-colors [direction:rtl] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 ${
                    isSelected
                      ? 'border-[#e2e8f0] !bg-[rgb(242,100,110)] text-white hover:!bg-[rgb(242,100,110)]'
                      : 'border border-[#e2e8f0] hover:bg-[#f1f5f9]'
                  }`}
                  aria-pressed={isSelected}
                >
                  {category.label}
                </a>
              )
            })}
          </div>
        )}

        {cards.cards.length > 0 && (
          <>
            {/* Show fallback message when displaying Sharon shows instead of central */}
            {isShowingFallbackResults && (
              <div className="mb-8 flex w-full flex-col items-center justify-center rounded-[11px] border border-[#606482]/50 py-3.5">
                <div className="flex gap-2 font-bold text-[#606482]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" className="flex-shrink-0">
                    <path fill="#fff" d="M4.047 2.066H9.93v9.67H4.047z" />
                    <path
                      fill="#F2646E"
                      d="M7 0a7 7 0 1 1 0 14A7 7 0 0 1 7 0Zm1.05 4.298c.52 0 .942-.361.942-.896s-.423-.896-.942-.896c-.52 0-.94.36-.94.896 0 .535.42.896.94.896Zm.183 5.626c0-.107.037-.385.016-.543l-.822.946c-.17.18-.383.303-.483.27a.203.203 0 0 1-.129-.23l1.37-4.327c.112-.55-.196-1.05-.849-1.114-.689 0-1.703.699-2.32 1.586 0 .106-.02.37.001.528l.821-.947c.17-.177.368-.302.468-.268a.2.2 0 0 1 .122.252L5.07 10.383c-.157.504.14.998.86 1.11 1.06 0 1.686-.682 2.304-1.569h-.001Z"
                    />
                  </svg>
                  <span>נראה שלא נמצאו הופעות במרכז</span>
                </div>
                <div className="text-[#606482]">אבל היי! יש לנו הופעות שוות בשרון</div>
              </div>
            )}

            {/* Featured Shows Grid - Matches client's product-list grid exactly */}
            <ul className="shows-grid m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(260px,1fr))] items-start gap-x-3 gap-y-5 p-0 px-1 [direction:rtl]">
              {cards.cards.map((card) => {
                return (
                  <li key={card.computed.id} className="w-full">
                    <ShowsCard data={card} categoryId={SHOWS_CATEGORY_ID} />
                  </li>
                )
              })}
            </ul>
            <style>
              {`
                .shows-grid {
                  direction: rtl !important;
                }
                @media (max-width: 930px) {
                  .shows-grid {
                    grid-template-columns: 1fr 1fr !important;
                    padding-inline: 12px !important;
                  }
                }
                @media (max-width: 768px) {
                  .shows-grid {
                    grid-template-columns: 1fr !important;
                    padding-inline: 12px !important;
                    padding-top: 10px !important;
                    gap: 24px !important;
                  }
                }
              `}
            </style>

            {cards.hasMore && (
              <div className="mt-12 text-center">
                <a
                  href={cards.seeAllUrl}
                  onClick={handleSeeAllClick}
                  className="ring-offset-background focus-visible:ring-ring bg-background inline-flex h-10 items-center justify-center gap-2 rounded-full border border-[#e2e8f0] !bg-[rgb(242,100,110)] px-6 py-3 text-base font-medium whitespace-nowrap text-white no-underline transition-colors [direction:rtl] hover:border-black hover:!bg-[rgb(242,100,110)] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                >
                  לכל ההופעות
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}

const filterByCategory = (cards: ProductCardDataShows[], categoryId: string | null): ProductCardDataShows[] => {
  if (!categoryId) {
    return cards
  }

  return cards.filter((card) => {
    // Category is stored in api.category
    const cardCategory = card.api.category

    return cardCategory === categoryId
  })
}

/**
 * Extract unique categories from shows data
 */
const extractAvailableCategories = (cards: ProductCardDataShows[]): Array<{ id: string; label: string }> => {
  const categorySet = new Set<string>()

  for (const card of cards) {
    const category = card.api.category

    if (category && category.trim()) {
      categorySet.add(category)
    }
  }

  // Convert to array and sort
  const categories = [...categorySet].toSorted().map((category) => {
    return {
      id: category,
      label: category, // Use the category name as-is (it's already in Hebrew)
    }
  })

  return categories
}

/**
 * Get category from URL path or context
 */
const getCategoryFromUrl = (contextType?: string): string | null => {
  if (typeof window === 'undefined') return null

  return getCategoryFromPathOrContext(window.location.pathname, contextType)
}
